import Immutable from 'immutable';
import { snakeCase } from 'lodash-es';
import { defaultAvatar, getColor, getIcon, isGuestAvatar } from 'ui-kit/scripts/guest-avatars';
import cloudinary from '../utils/cloudinary';
import { UserRole } from '../components/moderation-v2/comments-and-posts/state/types';

var UserFields = Immutable.Record({
  displayName: null,
  email: null,
  id: null,
  userId: null,
  imageId: defaultAvatar,
  isLoggedIn: false,
  socialNetworks: Immutable.List(),
  username: null,
  isEmailVerified: false,
  isOnline: false,
  type: null,
  isRegistered: false,
  isWhitelisted: false,
  isBlacklisted: false,
  note: null,
  roles: []
});

class User extends UserFields {
  parse(data) {
    if (!data) {
      return this;
    }

    var fields = {};

    [
      'displayName',
      'email',
      'id',
      'userId',
      'imageId',
      'isLoggedIn',
      'username',
      'isEmailVerified',
      'isOnline',
      'type',
      'isRegistered',
      'isWhitelisted',
      'isBlacklisted',
      'note'
    ].forEach(field => (fields[field] = data[field] !== undefined ? data[field] : this.get(field)));

    if (data.socialNetworks) {
      fields.socialNetworks = Immutable.List(data.socialNetworks.slice(0));
    } else {
      fields.socialNetworks = this.get('socialNetworks');
    }

    if (data.roles) {
      fields.roles = (data.roles || [])
        .map(role => role.replace(/-/g, '_').toLowerCase())
        // Filter only recognized UserRole defined roles
        .filter(role => UserRole[role.toUpperCase()] >= 0);
    } else {
      fields.roles = this.get('roles');
    }

    var newUser = new User(fields);
    return Immutable.is(this, newUser) ? this : newUser;
  }

  getThumbnailUrl(options) {
    return User.getThumbnailUrl(this.get('imageId'), options);
  }

  isUsernameSet() {
    var username = this.get('username');
    return !!username && username !== 'Guest';
  }

  isGuestAvatar() {
    return isGuestAvatar(this.get('imageId'));
  }

  getGuestIcon() {
    return getIcon(this.get('imageId'));
  }

  getGuestColor() {
    return getColor(this.get('imageId'));
  }

  isDefaultImage() {
    return this.get('imageId') === defaultAvatar;
  }

  getUsername() {
    if (this.isRegistered) {
      return `@${this.username}`;
    } else {
      return `Guest (${this.id})`;
    }
  }

  getDisplayName() {
    if (this.displayName) {
      return this.displayName;
    } else if (this.isRegistered) {
      return this.username;
    } else if (this.username) {
      // TODO: remove this when backend returs display name always
      return this.username.replace(/([A-Z])/g, ' $1').trim();
    } else {
      return '';
    }
  }

  getUserRole(overridedRoles) {
    return UserRole[
      (overridedRoles || this.roles).reduce(
        (acc, curr) => {
          const roleName = snakeCase(curr).toUpperCase();
          const currentRoleStrength = UserRole[roleName];
          return currentRoleStrength > acc ? currentRoleStrength : acc;
        },
        this.isRegistered ? UserRole.REGISTERED : UserRole.GUEST
      )
    ].toLowerCase();
  }

  toJSON() {
    return {
      displayName: this.get('displayName'),
      email: this.get('email'),
      id: this.get('id'),
      imageId: this.get('imageId'),
      isJournalist: this.get('isJournalist'),
      isCommunityModerator: this.get('isCommunityModerator'),
      isModerationViewer: this.get('isModerationViewer'),
      isLoggedIn: this.get('isLoggedIn'),
      social: this.get('social'),
      username: this.get('username'),
      isEmailVerified: this.get('isEmailVerified'),
      isOnline: this.get('isOnline'),
      type: this.get('type'),
      userId: this.userId,
      isRegistered: this.isRegistered
    };
  }

  static isStrongerRole(userA, userB) {
    const userARole = userA?.getUserRole();
    const userBRole = userB?.getUserRole();

    if (typeof userARole !== 'string' || typeof userBRole !== 'string') {
      return 1;
    }

    const userARoleStrength = UserRole[snakeCase(userARole).toUpperCase()];
    const userBRoleStrength = UserRole[snakeCase(userBRole).toUpperCase()];

    return userBRoleStrength - userARoleStrength;
  }

  static getThumbnailUrl(imageId, options) {
    return User.getThumbnailUrl(imageId, options);
  }

  static getGuestIcon(imageId) {
    return getIcon(imageId);
  }

  static fromJSON(json) {
    return new User().parse(json);
  }

  static isGuestAvatar(imageId) {
    return isGuestAvatar(imageId);
  }

  static isDefaultImage(imageId) {
    return imageId === defaultAvatar;
  }

  static getGuestColor(imageId) {
    return getColor(imageId);
  }

  static getThumbnailUrl(imageId, options) {
    // In case of guest user when extension is not specified, the requested extension should be svg (for predefined guest images).
    // if (guestImages.isGuestImage(imageId) && !options.extension) {
    //     options.extension = 'svg';
    // }
    options = Object.assign(
      {
        crop: 'thumb',
        gravity: 'face',
        height: 24,
        radius: 'max',
        width: 24
      },
      options
    );

    return imageId && cloudinary.url(imageId, options);
  }
}

export default User;
